import { extendTheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';
import preset from './presets';

// ════════════════════════════════════════════════════════════════════════
//  🎨  SINGLE ROOT THEME CONFIG — SOURCE OF TRUTH FOR BOTH THEMES
//  ------------------------------------------------------------------------
//  Dual theme (dark + light) driven entirely from this file. Components
//  consume SEMANTIC tokens (bg.surface, text.primary, …) which Chakra
//  compiles to CSS custom properties that flip on the color-mode class.
//
//  ★ THE COLOUR SCHEME ITSELF LIVES IN `theme/presets/`. ★
//    To re-skin the app, change ACTIVE in `presets/index.js` — nothing in
//    this file, and nothing in views/, needs to change. `primitives` below
//    is DERIVED from the active preset; it is a naming adapter, not a
//    palette. Do not paste hex values back into it.
//
//  Editing guide:
//    • Change the whole scheme  → `presets/index.js` (one line).
//    • Retune one scheme        → the preset file, e.g. `presets/apple-dark.js`.
//    • Re-map a ROLE            → `semanticTokens` (Tier 2) below.
//    • Never hardcode a hex in a component — add / reuse a semantic token.
//
//  Contrast (WCAG 2.2) — gated by `scripts/theme/contrast-check.js`.
// ════════════════════════════════════════════════════════════════════════

// ────────────────────────────────────────────────────────────────────────
//  TIER 1 — PRIMITIVES  (DERIVED from the active preset — see theme/presets/)
//
//  This object is a NAMING ADAPTER, not a palette. It exists because ~140
//  references below (semanticTokens, styles.global, the Button/Checkbox
//  variants) already speak in these names. Every value comes from the preset,
//  so changing ACTIVE in presets/index.js re-skins all of them at once.
//
//  ⚠ Do NOT paste hex values back in here — that would re-break the one-place
//  swap this indirection exists to provide. Put them in a preset instead.
// ────────────────────────────────────────────────────────────────────────
const primitives = {
	// Dark elevation ramp. Also published below as the Chakra `navy` colour
	// scale, which ~1,000 useColorModeValue('…','navy.800') call-sites resolve
	// against — hence the key name is retained even when the preset is black.
	navy: preset.surfaceScale,

	// Light-mode 'paper' ramp (Apple system greys).
	paper: preset.paper,

	// Text — dark steps softened (never pure #fff); light steps are Apple labels.
	ink: preset.ink,

	// Brand accent — ONE primary colour. The key name `gold` is RETAINED because
	// 240+ semantic/CSS references consume it by name (accent.gold, border.gold,
	// .gold-text, goldGlow, …); only the VALUE follows the preset.
	gold: {
		glow: preset.accent.glow,
		light: preset.accent.light,
		primary: preset.accent.primary,
		dark: preset.accent.dark,
		600: preset.accent.hover,
		// Accent as TEXT/ink. This slot is consumed as the LIGHT half of every
		// accent mm() pair below (text.accent, border.focus, focus.ring, …), so
		// it must be the darkened ink — the vivid primary is 2.2:1 on white and
		// fails AA. The dark halves keep the vivid primary.
		text: preset.accent.inkLight || preset.accent.primary,
		accent: preset.accent.primary, // = primary (icons / figures)
		fill: preset.accent.primary,   // = primary (solid fills) — dark text on top
		gradient: `linear-gradient(135deg, ${preset.accent.light} 0%, ${preset.accent.primary} 55%, ${preset.accent.gradientEnd} 100%)`,
	},

	// Feedback hues — Apple system colours, per mode.
	feedback: preset.feedback,
};

// mode helper — dark is the DEFAULT (guarantees dark fallback, no light flash)
const mm = (light, dark) => ({ default: dark, _light: light });
const alpha = (hex, a) => {
	const c = hex.replace('#', '');
	const r = parseInt(c.slice(0, 2), 16);
	const g = parseInt(c.slice(2, 4), 16);
	const b = parseInt(c.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};
/**
 * Opaque blend of `fg` over `bg` at ratio `a`.
 *
 * Opacity is the point. 61 tables render a `position: sticky` <Thead>; with a
 * translucent header the rows scroll visibly through it. So the accent tint is
 * baked against the surface here instead of being an rgba() wash.
 */
const blend = (fg, bg, a) => {
	const parts = (h) => {
		const c = h.replace('#', '');
		return [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16));
	};
	const [r1, g1, b1] = parts(fg);
	const [r2, g2, b2] = parts(bg);
	const mix = (x, y) => Math.round(x * a + y * (1 - a));
	return `#${[mix(r1, r2), mix(g1, g2), mix(b1, b2)]
		.map((v) => v.toString(16).padStart(2, '0'))
		.join('')}`;
};

// ────────────────────────────────────────────────────────────────────────
//  TIER 2 — SEMANTIC TOKENS  (intent-based, theme-agnostic names that
//  components consume). Same name → different primitive per theme.
//
//  DARK  base #0B1C2C · card #1A3550   |  LIGHT page #EFF1F4 · card #FCFCFD
//  key text/UI pairs all pass WCAG AA (see contrast comments).
// ────────────────────────────────────────────────────────────────────────
const semanticTokens = {
	colors: {
		// ── Surfaces ───────────────────────────────────────────────────
		'bg.base': mm(primitives.paper[100], primitives.navy[900]), // app background
		'bg.app': mm(primitives.paper[100], primitives.navy[900]), // alias (legacy)
		// Inset wells / zebra / subtle fills. The dark value MUST differ from
		// bg.surface (navy[800]) — they were identical once, and every card,
		// section well and row hover in dark mode went flat.
		'bg.subtle': mm(primitives.paper[50], primitives.navy[850]),
		'bg.surface': mm(primitives.paper[0], primitives.navy[800]), // cards / panels
		'bg.elevated': mm(primitives.paper[200], primitives.navy[700]), // hover / active / popovers
		'bg.input': mm(primitives.paper[0], primitives.navy[700]), // form fields
		'bg.sidebar': mm(primitives.paper[0], primitives.navy[900]),
		'bg.topbar': mm(primitives.paper[0], primitives.navy[800]),
		'bg.overlay': mm(alpha(primitives.navy[900], 0.45), alpha('#000000', 0.6)),

		// ── Content / text ─────────────────────────────────────────────
		'text.primary': mm(primitives.ink.lPrimary, primitives.ink.dPrimary), // 16.8 / 15.9
		'text.heading': mm(primitives.ink.lPrimary, primitives.ink.dPrimary), // alias
		'text.secondary': mm(primitives.ink.lSecondary, primitives.ink.dSecondary), // 7.3 / 9.6
		'text.body': mm(primitives.ink.lSecondary, primitives.ink.dSecondary), // alias
		'text.muted': mm(primitives.ink.lMuted, primitives.ink.dMuted), // 4.8 / 6.3
		'text.inverse': mm(primitives.paper[0], primitives.navy[900]),
		// Alias: live code writes `text.invert` (leadSetting) — same meaning.
		'text.invert': mm(primitives.paper[0], primitives.navy[900]),
		'text.accent': mm(primitives.gold.text, primitives.gold.primary), // ink #A85600 on light (5.3:1) / vivid #FF9500 on dark (7.85:1)
		// text ON a filled-orange element — near-black in BOTH modes (dark text on
		// the vivid #FF9500 fill → 8.35:1). Never white (white on orange fails).
		'text.onAccent': '#1A1400',
		// literal helpers (kept for legacy call-sites; prefer the roles above)
		'text.white': '#FFFFFF',
		'text.black': primitives.ink.lPrimary,

		// ── Borders ────────────────────────────────────────────────────
		'border.subtle': mm(primitives.paper[200], alpha(primitives.navy[100], 0.08)),
		'border.default': mm(primitives.paper[300], primitives.navy[600]),
		'border.strong': mm(primitives.paper[400], primitives.navy[400]),
		'border.focus': mm(primitives.gold.text, primitives.gold.primary),
		'border.gold': mm(primitives.gold.text, primitives.gold.primary),

		// ── Interactive / accent ───────────────────────────────────────
		'accent': mm(primitives.gold.accent, primitives.gold.primary), // vivid icon/figure accent
		'accent.gold': mm(primitives.gold.accent, primitives.gold.primary), // = vivid Apple orange
		// Apple-orange SOLID fill for toggles / checked controls / selected
		// fills — the one #FF9500 (both modes), always with near-black text on top.
		'accent.solid': mm(primitives.gold.fill, primitives.gold.primary),
		'accent.hover': mm(primitives.gold[600], primitives.gold.light), // #E68600 light / #FFB340 dark
		'accent.pressed': primitives.gold.dark, // #CC7700 (Apple active) — both modes
		'accent.subtle': mm(alpha(primitives.gold.primary, 0.12), alpha(primitives.gold.primary, 0.16)), // pale orange tint
		'accent.goldLight': primitives.gold.light,
		'accent.goldDark': primitives.gold.dark,
		'accent.glow': primitives.gold.glow,
		'focus.ring': mm(primitives.gold.text, primitives.gold.primary), // #FF9500 (>3:1 focus ring)

		// ── Secondary accent — Apple BLUE (the complement to orange) ────
		// Use for links, info, secondary highlights, secondary buttons.
		'secondary': mm('#0071E3', '#409CFF'), // 4.7:1 on white / 4.4:1 on navy card
		'secondary.subtle': mm(alpha('#0071E3', 0.12), alpha('#0A84FF', 0.16)),
		'secondary.solid': mm('#007AFF', '#0A84FF'), // Apple blue solid fill (white text)

		// ── Feedback ───────────────────────────────────────────────────
		'success': mm(primitives.feedback.successL, primitives.feedback.successD),
		'success.subtle': mm(alpha(primitives.feedback.successL, 0.12), alpha(primitives.feedback.successD, 0.15)),
		'warning': mm(primitives.feedback.warningL, primitives.feedback.warningD),
		'warning.subtle': mm(alpha(primitives.feedback.warningL, 0.12), alpha(primitives.feedback.warningD, 0.15)),
		'danger': mm(primitives.feedback.dangerL, primitives.feedback.dangerD),
		'danger.subtle': mm(alpha(primitives.feedback.dangerL, 0.12), alpha(primitives.feedback.dangerD, 0.15)),
		'info': mm(primitives.feedback.infoL, primitives.feedback.infoD),
		'info.subtle': mm(alpha(primitives.feedback.infoL, 0.12), alpha(primitives.feedback.infoD, 0.15)),

		// ── WhatsApp chat roles ────────────────────────────────────────
		// The chat panes mimic WhatsApp, whose identity carries its OWN light
		// and dark palettes (bubble greens, slate panels). These pairs are
		// WhatsApp's 2025–26 values — the unified green accent that replaced the
		// old teal, and the deeper dark mode — not the app preset's. Consumed via
		// `utils/helpers.js:whatsappColors`, which emits them as CSS vars so
		// every chat module flips with the mode. Chart-style canvases (the voice
		// waveform) read the same vars at runtime, so no component repeats a hex.
		'wa.app': mm('#FFFFFF', '#161717'),
		'wa.panel': mm('#EFEAE2', '#161717'),
		'wa.incoming': mm('#FFFFFF', '#242626'),
		'wa.outgoing': mm('#D9FDD3', '#144D37'),
		'wa.sidebar': mm('#FFFFFF', '#161717'),
		'wa.header': mm('#F0F2F5', '#1D1F1F'),
		'wa.composerBar': mm('#F0F2F5', '#1D1F1F'),
		'wa.input': mm('#FFFFFF', '#2A2A2A'),
		// The quoted-reply block sits INSIDE either bubble, so it is a tint of
		// the bubble rather than a colour of its own.
		'wa.reply': mm('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.06)'),
		'wa.replyBorder': mm('#06CF9C', '#06CF9C'),
		'wa.accent': mm('#1DAA61', '#21C063'),
		'wa.accentHover': mm('#17994F', '#1BAF55'),
		'wa.accentInk': mm('#FFFFFF', '#FFFFFF'),
		'wa.tickRead': mm('#53BDEB', '#53BDEB'),
		'wa.hover': mm('rgba(0, 0, 0, 0.05)', 'rgba(255, 255, 255, 0.06)'),
		'wa.selected': mm('rgba(0, 0, 0, 0.08)', 'rgba(255, 255, 255, 0.10)'),
		'wa.msgHover': mm('rgba(0, 0, 0, 0.03)', 'rgba(255, 255, 255, 0.04)'),
		'wa.ink': mm('#111B21', '#E9EDEF'),
		'wa.inkMuted': mm('#667781', '#8696A0'),
		'wa.datePill': mm('#FFFFFF', '#1D1F1F'),
		'wa.divider': mm('#E9EDEF', '#2A2A2A'),
		// The wallpaper doodles: a tint over `wa.panel`, masked by the pattern.
		'wa.doodle': mm('rgba(0, 0, 0, 0.06)', 'rgba(255, 255, 255, 0.035)'),
		'wa.menu': mm('#FFFFFF', '#233138'),
		'wa.chip': mm('#F0F2F5', '#2A2A2A'),
		'wa.chipActive': mm('#E7FCE3', '#103529'),
		'wa.chipActiveInk': mm('#008069', '#21C063'),
		'wa.recording': mm('#EA0038', '#F15C6D'),
		'wa.overlay': mm('rgba(0, 0, 0, 0.88)', 'rgba(0, 0, 0, 0.92)'),

		// ── ERP-specific roles (map onto the semantics above) ──────────
		'erp.sidebar.bg': mm(primitives.paper[0], primitives.navy[900]),
		'erp.sidebar.active': mm(primitives.gold.text, primitives.gold.primary),
		'erp.topbar.bg': mm(primitives.paper[0], primitives.navy[800]),
		'erp.table.bg': mm(primitives.paper[0], primitives.navy[800]),
		// Table headers carry a soft tint of the brand accent rather than a flat
		// grey — it ties tables to the product's identity and separates the header
		// row from the body without a heavy rule. Paired with 'text.accent' ink in
		// the Table theme below, which is AA on this wash in both modes.
		'erp.table.header': mm(blend(primitives.gold.primary, primitives.paper[0], 0.10), blend(primitives.gold.primary, primitives.navy[800], 0.14)),
	},

	shadows: {
		card: mm('0 1px 3px rgba(16,32,52,0.08)', '0 1px 3px rgba(0,0,0,0.5)'),
		soft: mm('0 8px 24px rgba(16,32,52,0.10)', '0 8px 24px rgba(0,0,0,0.4)'),
		hover: mm('0 12px 32px rgba(16,32,52,0.14)', '0 12px 32px rgba(0,0,0,0.6)'),
		deep: mm('0 24px 60px rgba(16,32,52,0.18)', '0 24px 60px rgba(0,0,0,0.7)'),
		goldGlow: '0 0 20px rgba(255, 149, 0, 0.45)', // orange brand glow (key name retained)
	},
};

// ════════════════════════════════════════════════════════════════════════
//  🧩  COMPONENT OVERRIDES — consume SEMANTIC tokens only (theme-agnostic)
// ════════════════════════════════════════════════════════════════════════
const components = {
	Button: {
		baseStyle: {
			fontWeight: 'semibold',
			borderRadius: '12px',
			transitionProperty: 'background, color, border-color, box-shadow, transform',
			transitionDuration: '200ms',
			_focusVisible: { boxShadow: '0 0 0 3px var(--chakra-colors-accent-subtle)' },
		},
		variants: {
			brand: {
				background: primitives.gold.gradient,
				color: '#1A1400', // 8.7:1 on gold
				padding: '16px 24px',
				borderRadius: '12px',
				_hover: {
					background: `linear-gradient(135deg, ${primitives.gold.light} 0%, ${primitives.gold.primary} 60%, ${primitives.gold.dark} 100%)`,
					boxShadow: 'goldGlow',
					transform: 'translateY(-1px)',
					// ★ Chakra's base style resets a hovered DISABLED button to
					// `bg: initial` — on a dark surface that is a black block. A
					// disabled Save must keep its own fill, only dimmed.
					_disabled: { transform: 'none', background: primitives.gold.gradient, boxShadow: 'none' },
				},
				_active: { background: primitives.gold.gradient, transform: 'translateY(0)' },
			},
			// No `colorScheme`, or the unopinionated 'gray' Chakra defaults to
			// when none is given, → the quiet, brand-outline look this theme
			// wants by default (ties into the hierarchy below: outline is a
			// utility level, not a second accent). Any OTHER explicit
			// `colorScheme` (red for destructive, green for success, …) means
			// the caller wants THAT color — restoring Chakra's own semantics
			// for that one case, so a `colorScheme='red'` Void/Reject button
			// isn't silently repainted brand-gold like everything else.
			outline: (props) => {
				const { colorScheme: c } = props;
				if (!c || c === 'gray') {
					return {
						border: '1px solid',
						borderColor: 'border.gold',
						color: 'text.accent',
						background: 'transparent',
						borderRadius: '12px',
						_hover: { background: 'accent.subtle' },
						_active: { background: 'accent.subtle' },
					};
				}
				return {
					border: '1px solid',
					borderColor: 'currentColor',
					color: mode(`${c}.600`, `${c}.300`)(props),
					background: 'transparent',
					borderRadius: '12px',
					_hover: { background: mode(`${c}.50`, 'whiteAlpha.100')(props) },
					_active: { background: mode(`${c}.100`, 'whiteAlpha.200')(props) },
				};
			},
			ghost: {
				color: 'text.secondary',
				background: 'transparent',
				_hover: { background: 'bg.elevated', color: 'text.accent' },
				_active: { background: 'bg.elevated', color: 'text.accent' },
			},

			// ── A HIERARCHY, NOT ONE COLOUR ──────────────────────────────
			// A toolbar where every button is the accent has no primary action:
			// the eye lands on "Bulk Assign" as readily as on "New". One accent
			// button per toolbar (`brand`); everything else steps down a level.
			//
			//   brand           the one thing the screen is for ("New", "Save")
			//   secondary       a real action that is not the main one (filled, neutral)
			//   neutralOutline  utilities — filter, export, paging (quiet outline)
			//   whatsapp        WhatsApp's own green, because that is what it is
			secondary: {
				background: 'bg.elevated',
				color: 'text.primary',
				border: '1px solid',
				borderColor: 'border.default',
				borderRadius: '12px',
				_hover: { background: 'bg.subtle', borderColor: 'border.focus', _disabled: { background: 'bg.elevated', borderColor: 'border.default' } },
				_active: { background: 'bg.subtle' },
			},
			neutralOutline: {
				border: '1px solid',
				borderColor: 'border.default',
				color: 'text.secondary',
				background: 'transparent',
				borderRadius: '12px',
				_hover: { background: 'bg.elevated', color: 'text.primary', borderColor: 'border.focus', _disabled: { background: 'transparent', borderColor: 'border.default', color: 'text.secondary' } },
				_active: { background: 'bg.elevated' },
			},
			whatsapp: {
				background: 'wa.accent',
				color: 'wa.accentInk',
				borderRadius: '12px',
				_hover: { background: 'wa.accentHover', transform: 'translateY(-1px)', _disabled: { transform: 'none', background: 'wa.accent' } },
				_active: { background: 'wa.accentHover', transform: 'translateY(0)' },
			},
		},
		defaultProps: { variant: 'brand', size: 'sm' },
	},

	Card: {
		baseStyle: {
			container: {
				background: 'bg.surface',
				borderRadius: '16px',
				border: '1px solid',
				borderColor: 'border.subtle',
				boxShadow: 'card',
				color: 'text.primary',
			},
		},
	},

	Input: {
		variants: {
			outline: {
				field: {
					background: 'bg.input',
					borderColor: 'border.default',
					color: 'text.primary',
					borderRadius: '10px',
					_hover: { borderColor: 'border.strong' },
					_focus: { borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' },
					_placeholder: { color: 'text.muted' },
				},
			},
		},
		defaultProps: { variant: 'outline' },
	},

	Select: {
		variants: {
			outline: {
				field: {
					background: 'bg.input',
					borderColor: 'border.default',
					color: 'text.primary',
					borderRadius: '10px',
					_hover: { borderColor: 'border.strong' },
					_focus: { borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' },
				},
			},
		},
		defaultProps: { variant: 'outline' },
	},

	Textarea: {
		variants: {
			outline: {
				background: 'bg.input',
				borderColor: 'border.default',
				color: 'text.primary',
				borderRadius: '10px',
				_hover: { borderColor: 'border.strong' },
				_focus: { borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' },
				_placeholder: { color: 'text.muted' },
			},
		},
		defaultProps: { variant: 'outline' },
	},

	Table: {
		// baseStyle, not just `simple`: 45 tables use `striped`/`unstyled`,
		// which would otherwise keep the stock grey header.
		baseStyle: {
			th: { background: 'erp.table.header', color: 'text.accent' },
		},
		variants: {
			simple: {
				th: {
					background: 'erp.table.header',
					color: 'text.accent',
					borderColor: 'border.default',
					fontWeight: 'semibold',
					letterSpacing: '0.05em',
				},
				td: { color: 'text.primary', borderColor: 'border.subtle' },
				tr: { _hover: { background: 'bg.elevated' } },
			},
		},
	},

	Badge: {
		variants: {
			erp: { background: 'bg.elevated', color: 'text.secondary', borderRadius: '6px' },
			gold: {
				background: 'accent.subtle',
				color: 'text.accent',
				border: '1px solid',
				borderColor: 'border.gold',
				borderRadius: '6px',
			},
		},
	},

	Tabs: {
		variants: {
			line: {
				tab: {
					color: 'text.secondary',
					borderColor: 'transparent',
					_selected: { color: 'text.accent', borderColor: 'border.focus' },
					_hover: { color: 'accent.hover' },
				},
			},
			'soft-rounded': {
				tab: {
					color: 'text.secondary',
					_selected: { background: 'accent.subtle', color: 'text.accent' },
				},
			},
		},
	},

	Menu: {
		baseStyle: {
			list: {
				background: 'bg.surface',
				borderColor: 'border.default',
				boxShadow: 'hover',
				borderRadius: '12px',
			},
			item: {
				background: 'transparent',
				color: 'text.primary',
				_hover: { background: 'bg.elevated', color: 'text.accent' },
				_focus: { background: 'bg.elevated' },
			},
		},
	},

	Modal: {
		baseStyle: {
			dialog: { background: 'bg.surface', borderRadius: '16px', boxShadow: 'deep' },
			header: {
				background: 'bg.subtle',
				borderRadius: '16px 16px 0 0',
				color: 'text.primary',
				borderBottom: '1px solid',
				borderColor: 'border.default',
			},
			footer: {
				background: 'bg.subtle',
				borderRadius: '0 0 16px 16px',
				borderTop: '1px solid',
				borderColor: 'border.default',
			},
			body: { color: 'text.primary' },
			overlay: { background: 'bg.overlay' },
		},
	},

	Tooltip: {
		baseStyle: {
			background: 'bg.elevated',
			color: 'text.primary',
			borderRadius: '8px',
			fontSize: '13px',
			boxShadow: 'soft',
		},
	},

	Drawer: {
		baseStyle: {
			dialog: { background: 'bg.surface' },
			header: { color: 'text.primary', borderBottom: '1px solid', borderColor: 'border.default' },
			body: { color: 'text.primary' },
		},
	},

	Divider: { baseStyle: { borderColor: 'border.subtle', opacity: 1 } },

	Switch: { baseStyle: { track: { _checked: { background: 'accent.solid' } } } },

	Checkbox: {
		baseStyle: {
			control: {
				borderColor: 'border.strong',
				_checked: {
					background: 'accent.solid', // vivid Apple orange
					borderColor: 'accent.solid',
					color: '#1A1400', // near-black check on vivid orange (8.35:1 light / 8.93:1 dark)
				},
			},
			label: { color: 'text.primary' },
		},
	},
};

// ════════════════════════════════════════════════════════════════════════
//  🔠  TYPOGRAPHY · 📐 SPACE · 🌑 SHADOWS (static)
// ════════════════════════════════════════════════════════════════════════
const typography = {
	fonts: {
		heading: `'Cairo', 'Montserrat', sans-serif`,
		body: `'Tajawal', 'Poppins', sans-serif`,
		mono: `'Fira Code', 'Courier New', monospace`,
	},
	fontSizes: {
		h1: 'clamp(40px, 5vw, 64px)',
		h2: 'clamp(28px, 3.5vw, 40px)',
		h3: '24px',
		body: '16px',
		small: '13px',
		xs: '11px',
	},
	lineHeights: { heading: '1.3', body: '1.6' },
	letterSpacings: { heading: '0', body: '0', wide: '0.05em' },
};

const space = { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px', xxl: '48px' };

// static shadow scale (theme-agnostic fallbacks; mode-aware ones live in
// semanticTokens.shadows above with the same keys)
const shadows = {
	card: '0 1px 3px rgba(0,0,0,0.4)',
	soft: '0px 8px 24px rgba(0, 0, 0, 0.3)',
	goldGlow: '0 0 20px rgba(255, 149, 0, 0.45)', // orange brand glow (key name retained)
	deep: '0px 20px 60px rgba(0, 0, 0, 0.6)',
	// Chakra's default focus ring is blue — re-tint it to the ACTIVE accent so
	// components that use boxShadow:'outline' on keyboard focus match the kit.
	// (This carried the retired #D4AF37 metallic gold long after the preset
	// moved to #FF9500 — derive, never paste, the accent here.)
	outline: `0 0 0 3px ${alpha(preset.accent.primary, 0.45)}`,
};

// ════════════════════════════════════════════════════════════════════════
//  🏗️  THEME EXPORT
// ════════════════════════════════════════════════════════════════════════
export const globalStyles = extendTheme({
	config: { useSystemColorMode: false, initialColorMode: 'dark', cssVarPrefix: 'chakra' },

	// ── Raw color SCALES — the second lever of the single-root system ──────
	// Legacy code themes via `useColorModeValue('<lightToken>', '<darkToken>')`.
	// The FIRST arg is a LIGHT value, the SECOND a DARK value, so these scales
	// are tuned per-role: `secondaryGray` = Apple LIGHT neutrals, `navy` = the
	// DARK surfaces, `brand` = the orange accent, `gray` = neutral text greys
	// (Apple label greys at 400–600). Remapping a scale here re-themes every
	// call-site that references it — no per-file edits. Status scales expose a
	// light (600/700, dark-enough for white) and dark (300/400, bright for
	// navy) variant so `useColorModeValue('x.600','x.300')` stays legible both.
	colors: {
		// Apple-orange accent (Horizon's `brand` role).
		// ONE-primary orange ramp: 400 = 500 = #FF9500 so `useColorModeValue(
		// 'brand.500','brand.400')` yields the same orange in both modes; 300 =
		// lighter, 600–900 = hover/pressed/deep shades. 50–200 = pale tints.
		// DERIVED from the preset — these were hardcoded orange, which meant
		// ~550 call-sites (`useColorModeValue('brand.500','brand.400')`,
		// `colorScheme='brand'`, `bgGradient='linear(…, brand.400, …)'`) kept the
		// old accent when the preset changed. `gold` is the legacy alias of the
		// same ramp and is retained because call-sites use both names.
		brand: { ...preset.accentScale },
		// `gold` additionally carries NAMED keys. ~65 call-sites across leave,
		// payroll and survey write `gold.primary` / `gold.light` / `gold.dark`;
		// with only numeric keys those resolved to nothing and Chakra emitted the
		// raw string — invalid CSS, silently dropped (empty progress fills, dead
		// hover borders). The named keys make those sites mean what they say.
		gold: {
			...preset.accentScale,
			primary: preset.accent.primary,
			light: preset.accent.light,
			dark: preset.accent.dark,
			glow: preset.accent.glow,
		},
		// Dark navy surfaces (used as the SECOND / dark arg).
		navy: { ...primitives.navy },
		// Apple LIGHT neutrals (used as the FIRST / light arg, and light-mode
		// standalone). Slots align to Horizon usage: 300 = page, 900 = text.
		secondaryGray: {
			50: '#FFFFFF', 100: '#ECECEF', 200: '#E8E8ED', 300: '#F5F5F7', 400: '#E8E8ED',
			500: '#6E6E73', 600: '#5B5B5F', 700: '#48484A', 800: '#3A3A3C', 900: '#1D1D1F',
		},
		// Neutral greys — light steps (50–300) + Apple label greys for dark
		// text (400 #AEAEB2, 500 #86868B, 600 #6E6E73) + dark surfaces (800/900).
		gray: {
			50: '#FAFAFC', 100: '#F5F5F7', 200: '#E8E8ED', 300: '#D2D2D7', 400: '#AEAEB2',
			500: '#86868B', 600: '#6E6E73', 700: '#48484A', 800: '#2C2C2E', 900: '#1D1D1F',
		},
		neutral: {
			50: '#FFFFFF', 100: '#F5F5F5', 200: '#E0E0E0', 300: '#B0B0B0', 400: '#909090',
			500: '#808080', 600: '#606060', 700: '#404040', 800: '#202020', 900: '#0B0E11',
		},
		// Status scales — each carries a bright DARK-mode step (300/400) and a
		// deep LIGHT-mode step (600/700), both AA on their respective surface.
		// 200/800/900 exist because Chakra's Badge `subtle`/`solid` variants and
		// the role-badge map in utils/colorUtils.js resolve them; when the scale
		// stopped at 100→300 those references emitted invalid CSS and the chips
		// rendered transparent with inherited text.
		red: { 50: '#FDECEA', 100: '#FDECEA', 200: '#FBC5C1', 300: '#F87171', 400: '#EF4444', 500: '#E5484D', 600: '#C53030', 700: '#B02525', 800: '#9B1C1C', 900: '#7F1D1D' },
		green: { 50: '#E6F7EF', 100: '#E6F7EF', 200: '#B8ECD4', 300: '#34D399', 400: '#10B981', 500: '#12A150', 600: '#157F55', 700: '#0F6B46', 800: '#0C5A3C', 900: '#094A32' },
		orange: { 50: '#FFF3E0', 100: '#FFF3E0', 200: '#FDE1B0', 300: '#FBBF47', 400: '#F59E0B', 500: '#E88C0A', 600: '#8A5A00', 700: '#7A4E00', 800: '#6B3F00', 900: '#5A3400' },
		blue: { 50: '#EAF2FD', 100: '#EAF2FD', 200: '#BFDBFE', 300: '#5AA9E6', 400: '#3B82F6', 500: '#2E7CE4', 600: '#2B6CB0', 700: '#245A94', 800: '#1E4E8C', 900: '#1A3E70' },
		// Aliases for scales that never existed but are written by live code —
		// each maps onto the closest real ramp so the sites regain their intent.
		// `greenish` (37 sites, lead-v2 contact buttons) and `indigo` (devIntel
		// markdown renderer) produced dropped declarations before.
		greenish: { 50: '#E6F7EF', 100: '#E6F7EF', 200: '#B8ECD4', 300: '#34D399', 400: '#10B981', 500: '#12A150', 600: '#157F55', 700: '#0F6B46', 800: '#0C5A3C', 900: '#094A32' },
		indigo: { 50: '#EAF2FD', 100: '#EAF2FD', 200: '#BFDBFE', 300: '#5AA9E6', 400: '#3B82F6', 500: '#2E7CE4', 600: '#2B6CB0', 700: '#245A94', 800: '#1E4E8C', 900: '#1A3E70' },
		softGray: {
			50: '#E7E7E7', 100: '#eeeeef', 200: '#BEBEBE', 300: '#969696', 400: '#F5F5F5',
			500: '#C4C4C4', 600: '#D9D9D9', 700: '#F6F6F6', 800: '#F2F2F2',
		},
	},

	semanticTokens,

	styles: {
		global: (props) => ({
			'*': { boxSizing: 'border-box' },
			'*::-webkit-scrollbar': { width: '6px', height: '6px' },
			// CSS vars, NOT mode(): Chakra v1 does not re-emit these global rules
			// when the toggle flips, so mode() values here stayed stale until the
			// next full reload (a black body behind a light app). The vars
			// re-resolve instantly in CSS.
			'*::-webkit-scrollbar-track': { background: 'var(--chakra-colors-bg-base)' },
			'*::-webkit-scrollbar-thumb': {
				background: 'var(--chakra-colors-border-strong)',
				borderRadius: '3px',
				_hover: { background: 'var(--chakra-colors-accent-gold)' },
			},
			body: {
				overflowX: 'hidden',
				background: 'var(--chakra-colors-bg-base)',
				color: 'var(--chakra-colors-text-primary)',
				fontFamily: typography.fonts.body,
				letterSpacing: typography.letterSpacings.body,
				lineHeight: typography.lineHeights.body,
				// theme-swap transition (color/bg only — never layout);
				// disabled under reduced-motion below
				transitionProperty: 'background-color, color',
				transitionDuration: '200ms',
				'[dir="rtl"] &': { textAlign: 'right' },
			},
			h1: {
				fontFamily: typography.fonts.heading,
				fontSize: typography.fontSizes.h1,
				fontWeight: '700',
				color: mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props),
				lineHeight: typography.lineHeights.heading,
				letterSpacing: typography.letterSpacings.heading,
			},
			h2: {
				fontFamily: typography.fonts.heading,
				fontSize: typography.fontSizes.h2,
				fontWeight: '600',
				color: mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props),
				lineHeight: typography.lineHeights.heading,
			},
			h3: {
				fontFamily: typography.fonts.heading,
				fontSize: typography.fontSizes.h3,
				fontWeight: '500',
				color: mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props),
			},
			p: { color: mode(primitives.ink.lSecondary, primitives.ink.dSecondary)(props), fontSize: typography.fontSizes.body },
			input: { color: mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props) },

			// ── Native <select> option menus ─────────────────────────────
			// OS-native dropdowns don't inherit Chakra styles. !important is used
			// deliberately to override the many components that hardcode
			// `style={{ color:'#000' }}` (invisible in dark) / navy option bgs on
			// their <option>s — so every dropdown flips with the colour mode.
			option: {
				background: `${mode(primitives.paper[0], primitives.navy[800])(props)} !important`,
				color: `${mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props)} !important`,
			},
			'option:disabled': { color: `${mode(primitives.ink.lMuted, primitives.ink.dMuted)(props)} !important` },

			// ── react-datepicker (3rd-party) light/dark theme ────────────
			// !important is required to override react-datepicker/dist CSS
			// regardless of import order. Values use the same primitives as the
			// rest of the theme, so both modes match the app surfaces.
			'.react-datepicker': {
				background: `${mode(primitives.paper[0], primitives.navy[800])(props)} !important`,
				color: `${mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props)} !important`,
				border: `1px solid ${mode(primitives.paper[300], primitives.navy[600])(props)} !important`,
				fontFamily: `${typography.fonts.body} !important`,
				borderRadius: '12px !important',
				overflow: 'hidden',
			},
			'.react-datepicker__header': {
				background: `${mode(primitives.paper[50], primitives.navy[900])(props)} !important`,
				borderBottom: `1px solid ${mode(primitives.paper[300], primitives.navy[600])(props)} !important`,
			},
			'.react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header': {
				color: `${mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props)} !important`,
			},
			'.react-datepicker__day-name': { color: `${mode(primitives.ink.lMuted, primitives.ink.dMuted)(props)} !important` },
			'.react-datepicker__day, .react-datepicker__time-name': {
				color: `${mode(primitives.ink.lSecondary, primitives.ink.dSecondary)(props)} !important`,
			},
			'.react-datepicker__day:hover, .react-datepicker__month-text:hover, .react-datepicker__quarter-text:hover, .react-datepicker__year-text:hover': {
				background: `${mode(primitives.paper[200], primitives.navy[700])(props)} !important`,
				color: `${mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props)} !important`,
			},
			'.react-datepicker__day--selected, .react-datepicker__day--keyboard-selected, .react-datepicker__day--in-range, .react-datepicker__day--in-selecting-range': {
				background: `${primitives.gold.primary} !important`, // the one orange
				color: '#1A1400 !important',
				fontWeight: '600',
			},
			'.react-datepicker__day--today': { color: `${primitives.gold.primary} !important`, fontWeight: '700' },
			'.react-datepicker__day--disabled': {
				color: `${mode(primitives.ink.lMuted, primitives.ink.dMuted)(props)} !important`,
				opacity: 0.5,
			},
			'.react-datepicker__navigation-icon::before, .react-datepicker__year-read-view--down-arrow, .react-datepicker__month-read-view--down-arrow': {
				borderColor: `${mode(primitives.ink.lSecondary, primitives.ink.dSecondary)(props)} !important`,
			},
			'.react-datepicker__triangle': { display: 'none !important' },
			'.react-datepicker-popper': { zIndex: '9999 !important' },
			'.react-datepicker__input-container input': {
				background: `${mode(primitives.paper[0], primitives.navy[700])(props)} !important`,
				color: `${mode(primitives.ink.lPrimary, primitives.ink.dPrimary)(props)} !important`,
				border: `1px solid ${mode(primitives.paper[300], primitives.navy[600])(props)} !important`,
				borderRadius: '10px !important',
				padding: '8px 12px',
			},

			html: { fontFamily: typography.fonts.body },
			// respect reduced-motion: kill the theme-swap transition
			'@media (prefers-reduced-motion: reduce)': {
				'*': { transitionDuration: '0.01ms !important' },
			},
			'.gold-text': {
				background: primitives.gold.gradient,
				WebkitBackgroundClip: 'text',
				WebkitTextFillColor: 'transparent',
				backgroundClip: 'text',
			},
			'.gold-glow': { boxShadow: shadows.goldGlow },
		}),
	},

	fonts: typography.fonts,
	fontSizes: typography.fontSizes,
	lineHeights: typography.lineHeights,
	letterSpacings: typography.letterSpacings,
	space,
	shadows,
	radii: { sm: '6px', md: '10px', lg: '12px', xl: '16px', '2xl': '20px', full: '9999px' },
	components,
});

// Raw exports for non-Chakra consumers (charts / inline styles).
export { primitives as tokens, typography, shadows, space };
